import { createAsyncThunk, createSlice } from "@reduxjs/toolkit"
import { logOut } from "./globalSlice"
import Api from "../api/api"
import { RootState } from "./store"
import { Credits, CreditsSummary } from "../api/models"
import { FetchableState, useFetchableState } from "./fetchableState"

export interface CreditsState extends FetchableState<Credits> {
    debug: string
    summary: FetchableState<CreditsSummary>
}

const initialState: CreditsState = {
    data: undefined,
    lastFetched: undefined,
    currentRequestId: undefined,
    error: undefined,
    debug: "credits state",
    summary: {
        data: undefined,
        currentRequestId: undefined,
        lastFetched: undefined,
        error: undefined,
    }
}

export const creditsSlice = createSlice({
    name: 'credits',
    initialState: initialState,
    reducers: {},
    extraReducers(builder) {
        builder.addCase(creditsThunk.pending, (state, action) => {
            if (state.currentRequestId)
                return
            state.currentRequestId = action.meta.requestId
        })
        builder.addCase(creditsThunk.fulfilled, (state, action) => {
            if (state.currentRequestId !== action.meta.requestId)
                return
            state.currentRequestId = undefined
            state.lastFetched = Date.now()
            state.data = action.payload.credits
        })
        builder.addCase(creditsThunk.rejected, (state, action) => {
            if (state.currentRequestId !== action.meta.requestId)
                return
            state.currentRequestId = undefined
        })
        builder.addCase(creditsSummaryThunk.pending, (state, action) => {
            if (state.summary.currentRequestId)
                return
            state.summary.error = undefined
            state.summary.currentRequestId = action.meta.requestId
        })
        builder.addCase(creditsSummaryThunk.fulfilled, (state, action) => {
            if (state.summary.currentRequestId !== action.meta.requestId)
                return
            state.summary.currentRequestId = undefined
            state.summary.lastFetched = Date.now()
            state.summary.data = action.payload.summary
        })
        builder.addCase(creditsSummaryThunk.rejected, (state, action) => {
            if (state.summary.currentRequestId !== action.meta.requestId)
                return
            state.summary.currentRequestId = undefined
            state.summary.error = action.error
        })        
        builder.addCase(logOut, (state: CreditsState) => {
            state = initialState;
        })
    },
})

export const creditsThunk = createAsyncThunk(
    'credits/fetchCredits',
    async ({ accessToken }: { accessToken: string }, { getState, requestId }) => {
        const state = getState() as RootState
        const currentRequestId = state.credits.currentRequestId
        if (currentRequestId !== requestId)
            throw new Error("Request outdated");
        return await Api.getCredits({ accessToken })
    }
)

export const creditsSummaryThunk = createAsyncThunk('credits/fetchCreditsSummary', async ({ accessToken }: { accessToken: string }, { getState, requestId }) => {
    const state = getState() as RootState;   // avoid sending request if it's going to be discarded anyway
    const currentRequestId = state.credits.summary.currentRequestId
    if (currentRequestId !== requestId)
        throw new Error("Request outdated");
    return await Api.getCreditsSummary({ accessToken })
})

const creditsSelector = (state: RootState) => state.credits

export const useCredits = (accessToken: string) => {
    return useFetchableState<Credits>({
        selector: creditsSelector,
        action: creditsThunk({ accessToken })
    })
}

export const useCreditsSummary = (accessToken: string) => {
    return useFetchableState<CreditsSummary>({
        selector: (state: RootState) => state.credits.summary,
        action: creditsSummaryThunk({ accessToken }),
    })
}

export default creditsSlice.reducer